import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MatrixBackground from '../components/wall/MatrixBackground';
import { Button } from '../components/ui/button';
import {
  Heart, Lock, Users, FileText, Image, Video, Link as LinkIcon,
  Loader2, ArrowLeft, ArrowRight, Zap, Shield, Play, Music
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WallDemoPage() {
  const navigate = useNavigate();
  const [wall, setWall] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDemoWall();
  }, []);

  const loadDemoWall = async () => {
    try {
      const res = await axios.get(`${API}/walls/demo/patriotnews`);
      setWall(res.data.wall);
      setPosts(res.data.posts);
    } catch (err) {
      console.error('Failed to load demo wall:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPostIcon = (type) => {
    switch (type) {
      case 'photo': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'link': return <Music className="w-4 h-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <MatrixBackground opacity={0.1} />
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a18] text-white">
      <MatrixBackground opacity={0.12} />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 border-b border-[#2d2d44]/50 bg-[#0a0a18]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/wall-info')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Wall Info
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full border border-emerald-500/30 font-medium">
              <Lock className="w-3 h-3 inline mr-1" /> Live Demo
            </span>
          </div>
          <Button
            onClick={() => navigate('/register')}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
          >
            Create Your Wall <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </header>

      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-20 pb-12">
        {/* Wall Header */}
        <div className="bg-gradient-to-br from-[#1a1a2e]/90 to-[#16162a]/90 backdrop-blur-xl rounded-2xl p-6 border border-emerald-500/20 mb-6 shadow-[0_0_30px_rgba(16,185,129,0.1)]">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4">
              {wall?.profile_image ? (
                <img src={wall.profile_image} alt={wall.name} className="w-20 h-20 rounded-full object-cover border-2 border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.3)]" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white text-3xl font-bold shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                  {wall?.name?.charAt(0) || 'P'}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-white">{wall?.name || 'My Patriot Wall'}</h1>
                <p className="text-emerald-400">@{wall?.slug || 'patriotnews'}</p>
              </div>
            </div>

            <div className="flex-1 flex flex-col md:flex-row md:items-center md:justify-end gap-4">
              <div className="flex items-center gap-6 text-gray-400">
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{wall?.subscriber_count || 0}</div>
                  <div className="text-xs">Subscribers</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{wall?.post_count || 0}</div>
                  <div className="text-xs">Posts</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{wall?.total_likes || 0}</div>
                  <div className="text-xs">Likes</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-emerald-500/20 text-gray-300 text-sm">
            This is a live demo of an encrypted Patriot Wall. Every post below is real content — 
            <span className="text-emerald-400 font-medium"> create your own wall</span> and start posting to your subscribers today.
          </div>
        </div>

        {/* Posts */}
        <div className="max-w-2xl mx-auto space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="relative group mb-4" data-testid={`demo-post-${post.post_type}`}>
              <div className="relative bg-gradient-to-br from-[#1a1a2e]/90 to-[#16162a]/90 backdrop-blur-xl rounded-2xl p-4 border border-emerald-500/15 shadow-[0_0_20px_rgba(16,185,129,0.05)] hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-300">
                
                {/* Post Type Badge */}
                {post.post_type !== 'text' && (
                  <div className="flex items-center gap-1 text-xs text-emerald-400 mb-2">
                    {getPostIcon(post.post_type)}
                    <span className="capitalize">{post.post_type === 'link' ? 'audio' : post.post_type}</span>
                  </div>
                )}

                {/* Content */}
                {post.content && (
                  <p className="text-gray-200 text-sm leading-relaxed mb-3 whitespace-pre-line">{post.content}</p>
                )}

                {/* Media */}
                {post.media_urls?.length > 0 && (
                  <div className="mb-3">
                    {post.post_type === 'video' ? (
                      <div className="rounded-xl overflow-hidden bg-black">
                        <video src={post.media_urls[0]} controls className="w-full max-h-72 object-contain" preload="metadata" />
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        {post.media_urls.map((url, idx) => (
                          <div key={idx} className="rounded-xl overflow-hidden">
                            <img src={url} alt="" className="w-full max-h-80 object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Audio player for link posts with mp3 */}
                {post.post_type === 'link' && post.link_url?.endsWith('.mp3') && (
                  <div className="mb-3 bg-[#252540] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <Music className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Audio Content</p>
                        <p className="text-xs text-gray-500">Encrypted audio stream</p>
                      </div>
                    </div>
                    <audio controls className="w-full" preload="metadata" style={{ height: '36px' }}>
                      <source src={post.link_url} type="audio/mpeg" />
                    </audio>
                  </div>
                )}

                {/* Link (non-audio) */}
                {post.post_type === 'link' && post.link_url && !post.link_url.endsWith('.mp3') && (
                  <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="block bg-[#252540] rounded-xl p-3 mb-3 hover:bg-[#2d2d50] transition-colors">
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-400 truncate">{post.link_url}</span>
                    </div>
                  </a>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-emerald-500/10">
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <Heart className="w-4 h-4" />
                    <span>{post.like_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">
                      {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <Lock className="w-3 h-3 text-emerald-500/50" />
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-500/3 to-cyan-500/3 blur-xl rounded-2xl" />
            </div>
          ))}
        </div>

        {/* CTA Bottom */}
        <div className="mt-12 text-center">
          <div className="p-8 bg-gradient-to-br from-emerald-500/10 to-[#6D4AFF]/10 border border-emerald-500/20 rounded-2xl">
            <Shield className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-2xl font-bold mb-2">Like what you see?</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Create your own encrypted wall. Share with your community. No ads, no tracking.
            </p>
            <Button
              onClick={() => navigate('/register')}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold px-8 py-5 text-lg rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.3)]"
              data-testid="demo-cta-btn"
            >
              <Zap className="w-5 h-5 mr-2" /> Start Your Wall Free
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
